<template>
    <div :class="{ 'ds-auth-phone': md }">
        <div :class="md ? 'ds-login-input-label pb-2' : 'ds-field-label pb-1'"> <slot></slot></div>
            <div :class="md ? 'ds-auth-phone-row' : 'flex items-center'">
                <dropdown v-model="phoneCode" :options="phoneCodes" :class="{'ds-auth-phone-code phone-code-input': md, 'w-16 min-w-16 max-w-16 phone-code-compact': !md}" :size>
                    {{ phoneCode.name }}
                </dropdown>
                <input ref="phoneInputRef" type="text" inputmode="tel" maxlength="15" :autocomplete="autocomplete" @input="filterInput" @change="syncFromInput"
                :class="md ? 'phone-input ds-auth-phone-number' : 'ds-field-input'"
                v-model="phone">
            </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue'

const model = defineModel()
const phone = ref('')

const { size, autocomplete } = defineProps<{
    size?: string;
    autocomplete?: string;
}>();

const md = computed(() => size === 'md')
const phoneInputRef = ref<HTMLInputElement | null>(null)

const phoneCodes = [
    { id: 1, name: '+7' },
    { id: 2, name: '+375' },
    { id: 3, name: '+996' }
]
const phoneCode = ref(phoneCodes[0])

const parsePhone = (rawValue: unknown) => {
    const modelValue = typeof rawValue === 'string' ? rawValue.trim() : ''

    if (!modelValue) {
        phone.value = ''
        return
    }

    const knownCode = phoneCodes.find((code) => modelValue.startsWith(code.name))

    if (knownCode) {
        phoneCode.value = knownCode
        phone.value = modelValue.slice(knownCode.name.length)
        return
    }

    phone.value = modelValue
}

const syncPhoneValue = (rawValue: string) => {
    const normalizedValue = rawValue.trim()
    const knownCode = phoneCodes.find((code) => normalizedValue.startsWith(code.name))

    if (knownCode) {
        phoneCode.value = knownCode
        phone.value = normalizedValue.slice(knownCode.name.length).replace(/\D/g, '')
        return
    }

    phone.value = normalizedValue.replace(/\D/g, '')
}

const filterInput = (event: Event) => {
    syncPhoneValue((event.target as HTMLInputElement).value)
}

const syncFromInput = () => {
    if (!phoneInputRef.value) return
    syncPhoneValue(phoneInputRef.value.value)
}

watch([phoneCode, phone], () => {
    if (phone.value.startsWith('+')) {
        model.value = phone.value
        return
    }

    model.value = phoneCode.value.name + phone.value
})

watch(
    () => model.value,
    (nextValue) => {
        const currentValue = phone.value.startsWith('+')
            ? phone.value
            : `${phoneCode.value.name}${phone.value}`

        if (typeof nextValue === 'string' && nextValue !== currentValue) {
            parsePhone(nextValue)
        }
    }
)

onMounted(() => {
    parsePhone(model.value)
})

function getValue() {
    return phone.value.startsWith('+')
        ? phone.value
        : `${phoneCode.value.name}${phone.value}`
}

defineExpose({
    syncFromInput,
    getValue
})
</script>

<style scoped>
input {
    background-color: #f2f2f2;
    color: #1F1F1F;
    &:focus {
        outline-color: #d2d2d2;
    }
}

.phone-input {
    height: var(--login-input-height);
    padding-left: var(--login-input-padding-x);
    padding-right: var(--login-input-padding-x);
    font-family: var(--font-family-base), sans-serif;
    font-size: var(--text-login-input-size);
    font-weight: var(--text-login-input-font-weight);
    line-height: var(--text-login-input-line-height);
}

.ds-auth-phone {
    width: 100%;
}

.ds-auth-phone-row {
    display: flex;
    align-items: center;
    width: 100%;
}

.ds-auth-phone-code {
    width: var(--auth-phone-code-width);
    min-width: var(--auth-phone-code-width);
    max-width: var(--auth-phone-code-width);
    flex: 0 0 var(--auth-phone-code-width);
}

.ds-auth-phone-number {
    flex: 1 1 auto;
    min-width: 0;
    width: 100%;
}

.phone-code-input :deep(> div:first-child) {
    height: var(--login-input-height);
    font-family: var(--font-family-base), sans-serif;
    font-size: var(--text-login-input-size);
    font-weight: var(--text-login-input-font-weight);
    line-height: var(--text-login-input-line-height);
    display: flex;
    align-items: center;
    padding-left: var(--login-input-padding-x);
    padding-right: var(--login-input-padding-x);
}

.ds-auth-phone-code :deep(> div:first-child) {
    justify-content: center;
    padding-left: 0;
    padding-right: 0;
    text-align: center;
}

.phone-code-compact :deep(> div:first-child) {
    display: flex;
    align-items: center;
    line-height: normal;
}
</style>
